/**
 * Hub — the thin product launcher (RBAC-1 build item 5).
 *
 * Deliberately thin. It reads entitlements and routes; it owns no product
 * state of its own. Three things it must get right:
 *
 *  - it is not on the path of a single-product user. `landingPathForUser`
 *    sends them straight into their one product, and a context link (an
 *    invite, an engagement URL) wins over both. Nobody should have to pick
 *    from a list of one.
 *  - un-bought products appear here greyed, not hidden — same rule as the nav.
 *    This page is where a firm discovers what else exists.
 *  - with nothing openable it says so plainly. That case used to land on
 *    /dashboard, which 403s and offers no way out.
 */
import { useCallback } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { hubProducts, type HubProduct } from '@/lib/productNav'
import { landingPathForRole } from '@/lib/roleLanding'

const LAST_PRODUCT_KEY = 'yansar.hub.lastProduct'

/** Remembering the last product is a convenience, never a gate — a read that
 *  throws (private mode, disabled storage) must not take the page down. */
function readLastProduct(): string | null {
  try {
    return window.localStorage.getItem(LAST_PRODUCT_KEY)
  } catch {
    return null
  }
}

export function rememberProduct(product: string): void {
  try {
    window.localStorage.setItem(LAST_PRODUCT_KEY, product)
  } catch {
    /* no-op: a launcher that cannot remember still launches */
  }
}

function ProductCard({ item, onOpen, isLast }: {
  item: HubProduct
  onOpen: (product: string) => void
  isLast: boolean
}) {
  const base = 'flex flex-col rounded-xl border p-5 text-left transition'
  if (item.accessible) {
    return (
      <Link
        to={item.href}
        onClick={() => onOpen(item.product)}
        className={`${base} border-gray-200 bg-white hover:border-[#0d1f3c] hover:shadow-sm`}
      >
        <span className="text-base font-semibold text-gray-900">{item.label}</span>
        <span className="mt-1 text-sm text-gray-600">
          {isLast ? 'Continue where you left off' : 'Open'}
        </span>
      </Link>
    )
  }
  return (
    <div
      aria-disabled="true"
      data-entitled={item.entitled ? 'true' : 'false'}
      className={`${base} cursor-not-allowed border-dashed border-gray-200 bg-gray-50`}
    >
      <span className="text-base font-semibold text-gray-400">{item.label}</span>
      <span className="mt-1 text-sm text-gray-400">
        {item.entitled
          ? 'Your firm has this — ask your partner for access'
          : 'Not included in your plan'}
      </span>
    </div>
  )
}

export function Hub() {
  const { user } = useAuth()
  const onOpen = useCallback((product: string) => rememberProduct(product), [])

  // No entitlement payload means /me has not reported one — the claims-only
  // identity built during E2E bootstrap. There is nothing to launch from, so
  // fall back to the role's landing page rather than render an empty hub.
  if (!user?.products) return <Navigate to={landingPathForRole(user?.role)} replace />

  const items = hubProducts(user.products)
  const openable = items.filter(p => p.accessible)
  const last = readLastProduct()

  // Last-used first, so returning to the hub returns you to your work. Ordering
  // only — nothing is added or removed by it.
  const ordered = [...items].sort((a, b) => {
    if (a.product === last && a.accessible) return -1
    if (b.product === last && b.accessible) return 1
    return 0
  })

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="text-lg font-semibold text-gray-900">Your products</h1>
      {openable.length === 0 ? (
        <p className="mt-2 text-sm text-gray-600">
          Nothing is open on this account yet. Ask your firm administrator to grant
          access, or contact us about the products below.
        </p>
      ) : (
        <p className="mt-2 text-sm text-gray-600">
          Pick up where you left off, or start somewhere new.
        </p>
      )}

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {ordered.map(item => (
          <ProductCard
            key={item.product}
            item={item}
            onOpen={onOpen}
            isLast={item.product === last}
          />
        ))}
      </div>

      <p className="mt-6 text-xs text-gray-500">
        Signed in as <span className="font-medium text-gray-700">{user.email}</span>
        {user.firm ? <> · {user.firm}</> : null}
      </p>
    </div>
  )
}
